"use client";

import { Button } from "@softmax/design-system";
import { colors, radii, spacing } from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { useId, useState } from "react";
import type { ReactNode } from "react";
import { MediaCarousel, NamePlateStage } from "@softmax/product-ux/catalog";
import type { CatalogMediaItem } from "@softmax/product-ux/catalog";

const styles = stylex.create({
  root: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: spacing.space8,
  },
  trigger: { position: "absolute", top: spacing.space8, right: spacing.space8 },
  tray: {
    overflow: "hidden",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: colors.borderStrong,
    borderRadius: radii.small,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: colors.selectedSurface,
    padding: spacing.space4,
  },
});

export function EditableStageView({
  items,
  name,
  uppercase,
  editing,
  board,
  replayEmbed,
  mediaTray,
  landscape,
}: Readonly<{
  items: readonly CatalogMediaItem[];
  name: string;
  uppercase: boolean;
  editing: boolean;
  board?: ReactNode;
  replayEmbed?: ReactNode;
  mediaTray?: ReactNode;
  /** Opt-in: forwarded to `MediaCarousel`. */
  landscape?: boolean | undefined;
}>) {
  const [trayOpen, setTrayOpen] = useState(false);
  const trayId = useId();
  const stage = items.length ? (
    <MediaCarousel
      items={items}
      name={name}
      uppercase={uppercase}
      board={board}
      replayEmbed={replayEmbed}
      landscape={landscape}
    />
  ) : (
    <NamePlateStage name={name} uppercase={uppercase} />
  );

  if (!editing) return stage;

  return (
    <div {...stylex.props(styles.root)}>
      {stage}
      <section {...stylex.props(trayOpen ? styles.tray : styles.trigger)}>
        <div {...stylex.props(trayOpen && styles.header)}>
          <Button
            size="sm"
            variant={trayOpen ? "quiet" : "secondary"}
            aria-expanded={trayOpen}
            aria-controls={trayId}
            onClick={() => setTrayOpen((open) => !open)}
          >
            Edit media <span aria-hidden="true">{trayOpen ? "↑" : "↓"}</span>
          </Button>
        </div>
        <div id={trayId} hidden={!trayOpen}>
          {mediaTray}
        </div>
      </section>
    </div>
  );
}
